import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  SearchOutlined,
  VisibilityOutlined,
  SwapHorizOutlined,
  ArchiveOutlined,
  PublishOutlined,
  DeleteOutlined,
  SchoolOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { roleAvatarColors } from '../../utils/roleColors';
import { getInitials } from '../../utils/getInitials';

interface WorkshopRow {
  id: string;
  title: string;
  status: 'draft' | 'published' | 'archived';
  stepsCount: number;
  sessionsCount: number;
  duration: number;
  createdAt: string;
  instructor: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const statusChipColor: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

const AdminWorkshops: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Table state
  const [workshops, setWorkshops] = useState<WorkshopRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState<Instructor | null>(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);

  // Instructors list (fetched once)
  const [instructors, setInstructors] = useState<Instructor[]>([]);

  // Dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState<WorkshopRow | null>(null);

  // Transfer form
  const [transferTarget, setTransferTarget] = useState<Instructor | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // Status change form
  const [newStatus, setNewStatus] = useState('');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Delete form
  const [deleteConfirmTitle, setDeleteConfirmTitle] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch instructors once on mount
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const res = await adminService.getInstructors();
        setInstructors(res.data);
      } catch {
        showNotification('Failed to load instructors', 'error');
      }
    };
    fetchInstructors();
  }, []);

  const fetchWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getWorkshops({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        instructorId: instructorFilter?.id || undefined,
        sortBy,
        sortOrder,
      });
      setWorkshops(res.data.workshops);
      setTotal(res.data.total);
    } catch {
      showNotification('Failed to load tech evals', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, instructorFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchWorkshops();
  }, [fetchWorkshops]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Stats computed from current page data
  const publishedCount = workshops.filter((w) => w.status === 'published').length;
  const draftCount = workshops.filter((w) => w.status === 'draft').length;
  const archivedCount = workshops.filter((w) => w.status === 'archived').length;

  // ---- TRANSFER ----
  const openTransfer = (w: WorkshopRow) => {
    setSelectedWorkshop(w);
    setTransferTarget(null);
    setTransferOpen(true);
  };

  const handleTransfer = async () => {
    if (!selectedWorkshop || !transferTarget) return;
    setTransferLoading(true);
    try {
      await adminService.transferWorkshop(selectedWorkshop.id, transferTarget.id);
      setTransferOpen(false);
      fetchWorkshops();
    } catch {
      showNotification('Failed to transfer tech eval', 'error');
    } finally {
      setTransferLoading(false);
    }
  };

  // ---- STATUS CHANGE ----
  const openStatusChange = (w: WorkshopRow) => {
    setSelectedWorkshop(w);
    setNewStatus(w.status === 'published' ? 'archived' : 'published');
    setStatusDialogOpen(true);
  };

  const handleStatusChange = async () => {
    if (!selectedWorkshop || !newStatus) return;
    setStatusChangeLoading(true);
    try {
      await adminService.updateWorkshopStatus(selectedWorkshop.id, newStatus);
      setStatusDialogOpen(false);
      fetchWorkshops();
    } catch {
      showNotification('Failed to update tech eval status', 'error');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // ---- DELETE ----
  const openDelete = (w: WorkshopRow) => {
    setSelectedWorkshop(w);
    setDeleteConfirmTitle('');
    setDeleteError('');
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedWorkshop) return;
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await adminService.deleteWorkshop(selectedWorkshop.id);
      setDeleteOpen(false);
      fetchWorkshops();
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete tech eval');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tech Eval Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={`Total: ${total}`} size="small" variant="outlined" />
          <Chip label={`Published: ${publishedCount}`} size="small" color="success" variant="outlined" />
          <Chip label={`Draft: ${draftCount}`} size="small" variant="outlined" />
          <Chip label={`Archived: ${archivedCount}`} size="small" color="warning" variant="outlined" />
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px' }} elevation={0}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search tech evals..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['all', 'draft', 'published', 'archived'].map((s) => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                size="small"
                variant={statusFilter === s ? 'filled' : 'outlined'}
                color={s === 'all' ? 'default' : statusChipColor[s] || 'default'}
                onClick={() => { setStatusFilter(s); setPage(0); }}
                sx={{ fontWeight: statusFilter === s ? 600 : 400 }}
              />
            ))}
          </Box>
          <Autocomplete
            size="small"
            options={instructors}
            getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName}`}
            value={instructorFilter}
            onChange={(_, val) => { setInstructorFilter(val); setPage(0); }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter by instructor" />
            )}
            sx={{ minWidth: 220 }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'title'} direction={sortBy === 'title' ? sortOrder : 'asc'} onClick={() => handleSort('title')}>
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'instructor'} direction={sortBy === 'instructor' ? sortOrder : 'asc'} onClick={() => handleSort('instructor')}>
                    Instructor
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sessions</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration (min)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'createdAt'} direction={sortBy === 'createdAt' ? sortOrder : 'asc'} onClick={() => handleSort('createdAt')}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : workshops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <SchoolOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No tech evals found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workshops.map((w) => {
                  const initials = getInitials(w.instructor.firstName, w.instructor.lastName);
                  return (
                    <TableRow
                      key={w.id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8f9fa' } }}
                      onClick={() => navigate(`/admin/workshops/${w.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {w.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: roleAvatarColors.instructor, fontSize: '0.7rem', fontWeight: 600 }}>
                            {initials}
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            {w.instructor.firstName} {w.instructor.lastName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={w.status}
                          size="small"
                          color={statusChipColor[w.status] || 'default'}
                          sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{w.stepsCount}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{w.sessionsCount}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{w.duration}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{dayjs(w.createdAt).fromNow()}</Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/admin/workshops/${w.id}`)}>
                            <VisibilityOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Transfer Ownership">
                          <IconButton size="small" onClick={() => openTransfer(w)}>
                            <SwapHorizOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={w.status === 'published' ? 'Archive' : 'Publish'}>
                          <IconButton size="small" onClick={() => openStatusChange(w)}>
                            {w.status === 'published' ? (
                              <ArchiveOutlined sx={{ fontSize: 18 }} />
                            ) : (
                              <PublishOutlined sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => openDelete(w)} sx={{ color: 'error.main' }}>
                            <DeleteOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* ---- TRANSFER OWNERSHIP DIALOG ---- */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Transfer Tech Eval Ownership</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Tech Eval: <strong>{selectedWorkshop?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current owner: <strong>{selectedWorkshop?.instructor.firstName} {selectedWorkshop?.instructor.lastName}</strong>
            {transferTarget && (
              <>
                {' \u2192 New owner: '}
                <strong>{transferTarget.firstName} {transferTarget.lastName}</strong>
              </>
            )}
          </Typography>
          <Autocomplete
            size="small"
            options={instructors.filter((i) => i.id !== selectedWorkshop?.instructor.id)}
            getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName} (${opt.email})`}
            value={transferTarget}
            onChange={(_, val) => setTransferTarget(val)}
            renderInput={(params) => (
              <TextField {...params} label="Select new owner" />
            )}
          />
          <Alert severity="warning">
            This will also transfer all sessions for this tech eval.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTransfer} disabled={transferLoading || !transferTarget}>
            {transferLoading ? <CircularProgress size={20} /> : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- CHANGE STATUS DIALOG ---- */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Change Tech Eval Status</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Tech Eval: <strong>{selectedWorkshop?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current status:{' '}
            <Chip label={selectedWorkshop?.status} size="small" color={statusChipColor[selectedWorkshop?.status || ''] || 'default'} sx={{ textTransform: 'capitalize' }} />
            {' \u2192 '}
            <Chip label={newStatus} size="small" color={statusChipColor[newStatus] || 'default'} sx={{ textTransform: 'capitalize' }} />
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={statusChangeLoading || newStatus === selectedWorkshop?.status}
          >
            {statusChangeLoading ? <CircularProgress size={20} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- DELETE WORKSHOP DIALOG ---- */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Delete Tech Eval</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            This will permanently delete all steps, sessions, and associated data.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently remove <strong>{selectedWorkshop?.title}</strong>. This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Type <strong>{selectedWorkshop?.title}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmTitle}
            onChange={(e) => setDeleteConfirmTitle(e.target.value)}
            placeholder={selectedWorkshop?.title}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading || deleteConfirmTitle !== selectedWorkshop?.title}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete Tech Eval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWorkshops;
